import { useEffect, useMemo, useState } from 'react';
import './App.scss';
import 'react-toastify/dist/ReactToastify.css';
import 'react-calendar/dist/Calendar.css';
import 'react-datepicker/dist/react-datepicker.css';
import { useIdleTimer } from 'react-idle-timer';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import {
  AuthLayout,
  CheckIn,
  DashboardLayout,
  Diagnostics,
  Login,
  VideoConsultingRoom,
  Customers,
  Orders,
  Consultations,
  HomeTest,
  Schedule,
  GuestVideoConsulting,
  ChatPage,
  FaceToFaceConsultingRoom,
  PrepareConsultation,
  Reports,
  VideoCall,
} from './screens/screens';
import { ToastContainer } from 'react-toastify';
import { useAppDispatch, useAppSelector } from './store/store';
import {
  selectIsLoggedIn,
  validateToken,
  selectUserRole,
  logout,
  allInfo,
  selectAccountInfo,
  IAccountInfo,
  IPerson,
  selectPersonLoggedIn,
  selectLocationId,
  selectIsMerchant,
  selectIsHealthpoint,
} from './store/slices/auth';
import Calendar from './screens/Calendar/Calendar';
import { authorizeClient } from './networking/client';
import ForgotPassword from './screens/authentication/ForgotPassword/ForgotPassword';
import ForgotPasswordOtp from './screens/authentication/ForgotPassword/ForgotPasswordOtp';
import SetYourNewPassword from './screens/authentication/ForgotPassword/SetYourNewPassword';
import ConfirmNewPassword from './screens/authentication/ForgotPassword/ConfirmNewPassword';
import { PreJoinScreen } from './components/UI/PreJoinScreen';
import { addToIndexedDb, getAllNotificationByPersonId } from './services/indexedDB';
import { Support } from './screens/Support/Support';
import { fetchServicesForLocation } from './store/slices/services';
import { DOCTOR_USER_ROLE, NURSE_USER_ROLE } from './constants/userRolesConstants';
import ConsultationParentComponent from './components/ConsultationParentComponent/ConsultationParentComponent';
import ConsultationMiddleware from './components/ConsultationMiddleware';
import PatientInformations from './components/PatientInformation/PatientInformation';

function App() {
  localStorage.removeItem('notification_data');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [eventSource, setEventSource] = useState<any>(null);
  const isLoggedIn: boolean = useAppSelector<boolean>(selectIsLoggedIn);
  const isMerchant: boolean = useAppSelector<boolean>(selectIsMerchant);
  const isHealthpoint: boolean = useAppSelector<boolean>(selectIsHealthpoint);
  const userRole: string = useAppSelector<string>(selectUserRole);
  const locationId: string = useAppSelector<string>(selectLocationId);
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const personLoggedIn = useAppSelector<IPerson>(selectPersonLoggedIn);
  const userProfile = {
    person_id: personLoggedIn ? parseFloat(personLoggedIn.person_external_id) : null,
  };

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(allInfo());
      // if (isHealthpoint === false) { //isMerchant === false is removed temporary
      //   alert('Access restricted: Only specific merchants can log into HealthPoint.');
      //   handleLogout();
      // }
    }
  }, [isLoggedIn, isMerchant, isHealthpoint, dispatch]);

  useEffect(() => {
    if (isLoggedIn && locationId) {
      dispatch(fetchServicesForLocation(locationId));
    }
  }, [isLoggedIn, dispatch, locationId]);

  useEffect(() => {
    dispatch(validateToken());
    localStorage.setItem('lang', localStorage.getItem('lang') ?? 'en');
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedIn && userProfile.person_id) {
      getAllNotificationByPersonId(userProfile.person_id);
      if (eventSource === null) {
        const eventSrc = new EventSource('/notification/subscribe', { withCredentials: true });

        eventSrc.onmessage = (event: any) => {
          const data = JSON.parse(event.data);
          if (data?.status === 'connected') setEventSource(eventSrc);
          if (data.conversation_id) addToIndexedDb({ ...data, person_id: userProfile.person_id });
          if (data?.type === 'consultation_notification') addToIndexedDb({ ...data });
        };

        eventSrc.onerror = (error: any) => {
          eventSrc.close();
          setEventSource(null);
        };

        eventSrc.addEventListener('ping', () => {
          // console.log('ping');
        });
        window.addEventListener('beforeunload', () => eventSrc.close());
      }
    } else {
      eventSource && eventSource?.close();
      setEventSource(null);
    }

    return () => {
      eventSource?.close();
      window.removeEventListener('beforeunload', () => eventSource?.close());
    };
  }, [isLoggedIn, userProfile.person_id, dispatch]);

  useEffect(() => {
    if (isLoggedIn) {
      let timer = setInterval(() => {
        callAuthorization();
      }, 60000);

      const callAuthorization = async () => {
        try {
          await authorizeClient.get('/ident/oauth/authorize');
          await authorizeClient.get('/core/person/authorization');
        } catch (_) { }
      };

      return () => clearInterval(timer);
    }
  }, []);

  const { getRemainingTime } = useIdleTimer({
    onIdle: () => {
      if (isLoggedIn && window.location.pathname !== '/web/prepare-consultation') {
        handleLogout();
      }
    },
    timeout: 600_000,
  });
  const handleLogout = async () => {
    try {
      await authorizeClient.get('/ident/oauth/logout');
      dispatch(logout());
      navigate('/web');
    } catch (_) { }
  };

  const routesBasedOnRole = useMemo(() => {
    if (accountInfo.account_number === '') {
      return <></>;
    }
    switch (userRole) {
      case 'admin':
        return (
          <Route path="/web">
            <Route path="/web/login" element={<Login />} />
            <Route element={<DashboardLayout />}>
              <Route path={'/web/reports'} element={<Reports />} />
              <Route element={<ConsultationMiddleware />}>
                <Route path="/web/" element={<Schedule />} />
                <Route path="/web/schedule" element={<Schedule />} />
                <Route path="/web/upcoming" element={<PatientInformations />} />
                <Route
                  path="/web/consultation/:sales_order_id/:sales_order_item_id"
                  element={<ConsultationParentComponent />}
                />
                <Route
                  path="/web/reports/edit/:sales_order_id/:sales_order_item_id"
                  // element={<EditConsulting />}
                  element={<ConsultationParentComponent />}
                />
                <Route path="/web/sales/customers" element={<Customers />} />
              </Route>
              <Route path="/web/consultations">
                <Route path={'reports'}>
                  <Route index element={<Consultations />} />
                  <Route
                    path={':sales_order_id/:sales_order_item_id'}
                    element={<Consultations />}
                  />
                </Route>
              </Route>
              <Route path="/web/prepare-consultation" element={<PrepareConsultation />} />
              {accountInfo?.supports_chat && <Route path="/web/chat" element={<ChatPage />} />}
              <Route path="/web/diagnostics/reports" element={<Diagnostics />} />
              <Route path="/web/diagnostics/check-in" element={<CheckIn />} />
              <Route path="/web/diagnostics/home-test" element={<HomeTest />} />
              <Route path="/web/sales" />
              <Route path="/web/sales/orders" element={<Orders />} />
              <Route path="/web/settings" element={<div />} />
              <Route path="/web/calendar" element={<Calendar />} />
              <Route path="/web/support" element={<Support />} />
              <Route path="*" element={<Navigate to="/web" />} />
            </Route>
            <Route path="/web/video-consulting-room" element={<VideoConsultingRoom />} />
            <Route path="/web/facetoface-consulting-room" element={<FaceToFaceConsultingRoom />} />
            <Route path="/web/guest-video-consulting-room" element={<GuestVideoConsulting />} />
            <Route path={'/web/video-call'} element={<VideoCall />} />
            <Route path="*" element={<Navigate to="/web" />} />
          </Route>
        );
      case 'diagnostic employee':
        return (
          <Route path="/web/">
            <Route path="/web/login" element={<Login />} />
            <Route path="/web/" element={<DashboardLayout />}>
              <Route path="/web/" element={<div />} />
              <Route path={'/web/reports'} element={<Reports />} />
              <Route path="/web/diagnostics/reports" element={<Diagnostics />} />
              <Route path="/web/diagnostics/home-test" element={<HomeTest />} />
              <Route path="/web/diagnostics/check-in" element={<CheckIn />} />
            </Route>
            <Route path="*" element={<Navigate to="/web" />} />
          </Route>
        );
      case DOCTOR_USER_ROLE:
        return (
          <Route path="/web/">
            <Route path="/web/login" element={<Login />} />
            <Route path="/web/" element={<DashboardLayout />}>
              <Route path={'/web/reports'} element={<Reports />} />
              <Route element={<ConsultationMiddleware />}>
                <Route path="/web/" element={<Schedule />} />
                <Route path="/web/schedule" element={<Schedule />} />
                <Route path="/web/upcoming" element={<PatientInformations />} />
                <Route
                  path="/web/consultation/:sales_order_id/:sales_order_item_id"
                  element={<ConsultationParentComponent />}
                />
                <Route
                  path="/web/reports/edit/:sales_order_id/:sales_order_item_id"
                  // element={<EditConsulting />}
                  element={<ConsultationParentComponent />}
                />
              </Route>
              <Route path="/web/consultations">
                <Route path={'reports'}>
                  <Route index element={<Consultations />} />
                  <Route
                    path={':sales_order_id/:sales_order_item_id'}
                    element={<Consultations />}
                  />
                </Route>
              </Route>
              <Route path="/web/prepare-consultation" element={<PrepareConsultation />} />
              {accountInfo?.supports_chat && <Route path="/web/chat" element={<ChatPage />} />}
            </Route>
            <Route path="/web/video-consulting-room" element={<VideoConsultingRoom />} />
            <Route path="/web/guest-video-consulting-room" element={<GuestVideoConsulting />} />
            <Route path="/web/facetoface-consulting-room" element={<FaceToFaceConsultingRoom />} />
            <Route path={'/web/video-call'} element={<VideoCall />} />
            <Route path="*" element={<Navigate to="/web" />} />
          </Route>
        );
      case 'sales employee':
        return (
          <Route path="/web/">
            <Route path="/web/login" element={<Login />} />
            <Route path="/web/" element={<DashboardLayout />}>
              <Route path="/web/sales/customers" element={<Customers />} />
              <Route path="/web/sales/orders" element={<Orders />} />
            </Route>
            <Route path="*" element={<Navigate to="/web" />} />
          </Route>
        );
      case 'care center agent':
        return (
          <Route path="/web/">
            <Route path="/web/login" element={<Login />} />
            <Route path="/web/" element={<DashboardLayout />}>
              <Route element={<ConsultationMiddleware />}>
                <Route path="/web/" element={<Schedule />} />
                <Route path="/web/schedule" element={<Schedule />} />
                <Route path="/web/upcoming" element={<PatientInformations />} />
                <Route
                  path="/web/consultation/:sales_order_id/:sales_order_item_id"
                  element={<ConsultationParentComponent />}
                />
              </Route>
              {accountInfo?.supports_chat && <Route path="/web/chat" element={<ChatPage />} />}
              {/* <Route path="/web/consultations">
                <Route path={'upcoming'}>
                  <Route index element={<VideoConsulting />} />
                  <Route
                    path={':sales_order_id/:sales_order_item_id'}
                    element={<VideoConsulting />}
                  />
                </Route>
              </Route> */}
              {/* <Route path="/web/calendar" element={<Calendar />} /> */}
              <Route path="/web/support" element={<Support />} />
              <Route path={'/web/video-call'} element={<VideoCall />} />
            </Route>
            <Route path="*" element={<Navigate to="/web" />} />
          </Route>
        );
      case NURSE_USER_ROLE:
        return (
          <Route path="/web/">
            <Route path="/web/login" element={<Login />} />
            <Route path="/web/" element={<DashboardLayout />}>
              <Route element={<ConsultationMiddleware />}>
                <Route path="/web/" element={<Schedule />} />
                <Route path="/web/schedule" element={<Schedule />} />
                {accountInfo?.supports_chat && <Route path="/web/chat" element={<ChatPage />} />}
                <Route path="/web/upcoming" element={<PatientInformations />} />
                <Route
                  path="/web/consultation/:sales_order_id/:sales_order_item_id"
                  element={<ConsultationParentComponent />}
                />
                <Route
                  path="/web/reports/edit/:sales_order_id/:sales_order_item_id"
                  // element={<EditConsulting />}
                  element={<ConsultationParentComponent />}
                />
                <Route path="/web/sales/customers" element={<Customers />} />
              </Route>
              <Route path={'/web/reports'} element={<Reports />} />
              <Route path="/web/consultations">
                <Route path={'reports'}>
                  <Route index element={<Consultations />} />
                  <Route
                    path={':sales_order_id/:sales_order_item_id'}
                    element={<Consultations />}
                  />
                </Route>
              </Route>
              <Route path="/web/prejoin-consultation" element={<PreJoinScreen />} />
              <Route path="/web/prepare-consultation" element={<PrepareConsultation />} />
              <Route path="/web/diagnostics/reports" element={<Diagnostics />} />
              <Route path="/web/diagnostics/check-in" element={<CheckIn />} />
              <Route path="/web/diagnostics/home-test" element={<HomeTest />} />
              <Route path="/web/sales/orders" element={<Orders />} />
              {/* <Route path="/web/calendar" element={<Calendar />} /> */}
              <Route path="/web/support" element={<Support />} />
              <Route path="*" element={<div />} />
            </Route>
            <Route path="/web/facetoface-consulting-room" element={<FaceToFaceConsultingRoom />} />
            <Route path="/web/guest-video-consulting-room" element={<GuestVideoConsulting />} />
            <Route path="*" element={<Navigate to="/web" />} />
          </Route>
        );

      default:
        return null;
    }
  }, [userRole, accountInfo]);

  return (
    <main>
      <Routes>
        {!isLoggedIn ? (
          <Route path="" element={<AuthLayout />}>
            <Route path="/web/login" element={<Login />} />
            <Route path="/web/forgot-password" element={<ForgotPassword />} />
            <Route path="/web/forgot-password-otp" element={<ForgotPasswordOtp />} />
            <Route path="/web/forget/set-new-password" element={<SetYourNewPassword />} />
            <Route path="/web/password-reset-confirmed" element={<ConfirmNewPassword />} />
            <Route path="*" element={<Navigate to="/web" />} />
          </Route>
        ) : (
          <Route>{routesBasedOnRole}</Route>
        )}
        <Route path={'/'} element={<Navigate to="/web" />} />
      </Routes>
      <ToastContainer />
    </main>
  );
}

export default App;
